"use client";
import {
  address,
  getBase58Decoder,
  getTransactionDecoder,
  signature as parseSignature,
  type Instruction,
  type Signature,
} from "@solana/kit";
import type { InstructionsWithAccountsAndData } from "@ellipsis-labs/rise";

import { isSponsorshipEnabled } from "@/lib/config";
import { buildUnsignedTransaction } from "./buildTransaction";
import { addLighthouseClockAssertion } from "./lighthouse";
import { getSolanaRpc } from "./rpc";
import { getFeePayer, sponsorTransaction } from "./sponsorship";
import type { SolanaSigner } from "./useSolanaWallet";

export interface SubmitOptions {
  /** Defaults to "sponsored" unless NEXT_PUBLIC_SPONSORSHIP_ENABLED disables it. */
  mode?: "self" | "sponsored";
  priorityFee?: number;
}

export interface SubmitResult {
  signature: string;
}

const signatureFromBytes = (bytes: Uint8Array): Signature =>
  parseSignature(getBase58Decoder().decode(bytes));

/** Poll SignatureStatuses until confirmed, or bail after ~10 retries @ 700ms. */
export const confirmSignature = async (signature: Signature): Promise<void> => {
  const rpc = getSolanaRpc();
  const maxAttempts = 12;
  const delayMs = 700;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const response = await rpc.getSignatureStatuses([signature]).send();
    const status = response.value[0];
    if (status !== null && status !== undefined) {
      if (status.err !== null) {
        throw new Error(
          `Transaction failed on-chain: ${JSON.stringify(status.err)}`
        );
      }
      if (
        status.confirmationStatus === "confirmed" ||
        status.confirmationStatus === "finalized"
      ) {
        return;
      }
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  throw new Error(
    `Timed out waiting for confirmation of signature ${String(signature)}`
  );
};

/**
 * Self-paid path: the user signs and submits the transaction themselves.
 * `signAndSendTransaction` bundles signing + broadcast, returning signature
 * bytes (first 64 bytes of the packet).
 */
export const submitSelfPaid = async (
  instructions: readonly Instruction[],
  signer: SolanaSigner,
  options: Pick<SubmitOptions, "priorityFee"> = {}
): Promise<SubmitResult> => {
  const { bytes } = await buildUnsignedTransaction(
    signer.address,
    instructions,
    { priorityFee: options.priorityFee }
  );
  const signatureBytes = await signer.signAndSendTransaction(bytes);
  const signature = signatureFromBytes(signatureBytes);
  await confirmSignature(signature);
  return { signature };
};

/**
 * Sponsored path: fetches a sponsorship fee payer, builds the transaction with
 * it as fee payer, has the user sign it, extracts just the user's signature,
 * and hands the signed tx + user signature off to the sponsorship service.
 *
 * The server re-signs with the sponsorship fee-payer keypair and broadcasts.
 */
export const submitSponsored = async (
  instructions: readonly Instruction[],
  signer: SolanaSigner,
  options: Pick<SubmitOptions, "priorityFee"> = {}
): Promise<SubmitResult> => {
  if (!isSponsorshipEnabled()) {
    return submitSelfPaid(instructions, signer, {
      priorityFee: options.priorityFee,
    });
  }

  const feePayer = await getFeePayer({ userPubkey: signer.address });
  const safeInstructions = addLighthouseClockAssertion(instructions);
  const { bytes, slot } = await buildUnsignedTransaction(
    feePayer.payer,
    safeInstructions,
    {
      priorityFee: options.priorityFee,
      version: "legacy",
    }
  );

  const signedBytes = await signer.signTransaction(bytes);

  const userSignature = extractUserSignature(signedBytes, signer.address);
  const transactionBase64 = bytesToBase64(signedBytes);

  const { signature } = await sponsorTransaction({
    transaction: transactionBase64,
    userSignature,
    userPubkey: signer.address,
    slot,
  });

  await confirmSignature(parseSignature(signature));
  return { signature };
};

const isInstructionList = (
  value:
    | InstructionsWithAccountsAndData
    | readonly InstructionsWithAccountsAndData[]
): value is readonly InstructionsWithAccountsAndData[] => Array.isArray(value);

export const submitInstruction = async (
  instructions:
    | InstructionsWithAccountsAndData
    | readonly InstructionsWithAccountsAndData[],
  signer: SolanaSigner,
  options: SubmitOptions = {}
): Promise<SubmitResult> => {
  const list = isInstructionList(instructions) ? instructions : [instructions];

  const mode = isSponsorshipEnabled() ? (options.mode ?? "sponsored") : "self";
  if (mode === "sponsored") {
    try {
      return await submitSponsored(list, signer, {
        priorityFee: options.priorityFee,
      });
    } catch (err) {
      // Fall back only when sponsorship is unavailable. Sponsorship validation
      // rejections should stay visible; v2 also does not silently self-pay
      // when the sponsor refuses a transaction.
      if (shouldFallbackToSelfPaid(err)) {
        console.warn(
          "[submitInstruction] sponsorship unavailable, falling back to self-paid",
          err
        );
        return submitSelfPaid(list, signer, {
          priorityFee: options.priorityFee,
        });
      }
      throw err;
    }
  }
  return submitSelfPaid(list, signer, {
    priorityFee: options.priorityFee,
  });
};

/**
 * Extract the single user signature from a signed transaction so the sponsor
 * can verify it. `@solana/kit` decodes the signatures into a map keyed by the
 * signer address; the wallet adapter only populates the user's slot, leaving
 * the fee-payer slot for the sponsor to fill in server-side.
 */
const extractUserSignature = (
  signedTxBytes: Uint8Array,
  userAddress: string
): string => {
  const decoded = getTransactionDecoder().decode(signedTxBytes);
  const rawSignature = decoded.signatures[address(userAddress)];
  if (rawSignature === undefined || rawSignature === null) {
    throw new Error(
      `Signed transaction missing signature for ${userAddress}; did the wallet refuse to sign?`
    );
  }
  return getBase58Decoder().decode(rawSignature);
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  if (typeof btoa === "function") {
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i] ?? 0);
    }
    return btoa(binary);
  }
  if (typeof Buffer === "undefined") {
    throw new Error("No base64 encoder available");
  }
  return Buffer.from(bytes).toString("base64");
};

const shouldFallbackToSelfPaid = (err: unknown): boolean => {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return (
    lower.includes("sponsorship url not configured") ||
    lower.includes("failed to fetch")
  );
};

/** Convenience: round-trip base58 → Signature brand for callers. */
export const signatureFromString = (sig: string): Signature =>
  parseSignature(sig);
